import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { isEventbriteEnabled } from "../eventbrite/eventbriteFeature";
import EventbriteImportDialog from "../eventbrite/EventbriteImportDialog";
import { toast } from "react-toastify";
import { useAuth } from "../auth/AuthContext";
import Footer from "../components/Footer";
import Header from "../components/Header";
import SuccessToast, { useSuccessToast } from "../components/SuccessToast";
import EventCardsGrid from "./EventCardsGrid";
import { EventsListPageSkeleton } from "./EventPageSkeletons";
import { clearStaleBodyScrollLock } from "./clearStaleBodyScrollLock";
import {
  deleteEvent,
  getCachedEvents,
  listEvents,
  subscribeEventsCache,
} from "./eventsService";
import "./Events.css";

const CreateEventPlaceholderCard = () => (
  <Link className="event-card events-placeholder-card" to="/events/new">
    <span className="events-placeholder-plus" aria-hidden="true" />
    <div className="event-card-content">
      <h2>New event from Excel</h2>
      <p className="events-placeholder-copy">
        Upload a spreadsheet with your attendees to create an event
      </p>
    </div>
  </Link>
);

const EventsListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const eventbriteEnabled = isEventbriteEnabled();
  const [eventbriteOpen, setEventbriteOpen] = useState(false);
  const cachedEvents = getCachedEvents(user.id);
  const hasCachedEvents = cachedEvents !== undefined;
  const [events, setEvents] = useState(() => cachedEvents ?? []);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(() => !hasCachedEvents);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [isDeletingEvent, setIsDeletingEvent] = useState(false);
  const { showSuccessToast, successToast } = useSuccessToast();

  useEffect(() => {
    clearStaleBodyScrollLock();
    const timeoutId = window.setTimeout(clearStaleBodyScrollLock, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => subscribeEventsCache(user.id, setEvents), [user.id]);

  useEffect(() => {
    let isMounted = true;
    const cachedEventsForUser = getCachedEvents(user.id);
    const hasCachedEventsForUser = cachedEventsForUser !== undefined;

    if (hasCachedEventsForUser) {
      setEvents(cachedEventsForUser);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError("");

    const loadEvents = async () => {
      try {
        const nextEvents = await listEvents(user.id);
        if (isMounted) {
          setEvents(nextEvents);
          setError("");
        }
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        if (!hasCachedEventsForUser) {
          setError(loadError.message || "Could not load events.");
        } else {
          console.error("Could not refresh events.", loadError);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, [user.id]);

  if (isLoading) {
    return <EventsListPageSkeleton />;
  }

  const handleDeleteConfirm = async () => {
    if (!eventPendingDelete) return;

    setIsDeletingEvent(true);
    try {
      await deleteEvent(eventPendingDelete.id, user.id);
      setEvents((currentEvents) =>
        currentEvents.filter((event) => event.id !== eventPendingDelete.id)
      );
      setEventPendingDelete(null);
      showSuccessToast("Event deleted");
    } catch (deleteError) {
      toast.error(deleteError.message || "Could not delete event.");
    } finally {
      setIsDeletingEvent(false);
    }
  };

  return (
    <div className="events-page">
      <Header />
      <main className="events-shell">
        <div className="events-container">
          <div className="events-header">
            <div>
              <h1>Events</h1>
              <p>Create and manage badge projects for each event</p>
            </div>
            <div className="events-header-actions">
              <Link className="primary" to="/events/new">New event from Excel</Link>
              {eventbriteEnabled && <button className="button" type="button" onClick={() => setEventbriteOpen(true)}>
                Import from Eventbrite
              </button>}
            </div>
          </div>

          {error && <div className="event-error" role="alert">{error}</div>}

          {!error && (
            <div className="events-grid">
              {events.length > 0 && (
                <EventCardsGrid
                  events={events}
                  onEventDelete={setEventPendingDelete}
                  showEventDetails
                  useFirstAttendeePreview
                  asFragment
                />
              )}
              <CreateEventPlaceholderCard />
            </div>
          )}
        </div>
      </main>
      <Footer />
      {eventbriteEnabled && <EventbriteImportDialog
        open={eventbriteOpen}
        onClose={() => setEventbriteOpen(false)}
        onImport={(result) => {
          setEventbriteOpen(false);
          navigate("/events/new", { state: { eventbriteImport: result } });
        }}
      />}
      <Dialog
        classes={{ paper: "event-delete-dialog" }}
        open={Boolean(eventPendingDelete)}
        onClose={() => {
          if (!isDeletingEvent) {
            setEventPendingDelete(null);
          }
        }}
      >
        <DialogContent>
          <div className="event-delete-dialog-content">
            <h2>Delete event?</h2>
            <p>
              This will permanently delete{" "}
              <strong>{eventPendingDelete?.name}</strong>.
            </p>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            className="button"
            disabled={isDeletingEvent}
            onClick={() => setEventPendingDelete(null)}
            type="button"
          >
            Cancel
          </button>
          <button
            className="button event-delete-confirm-button"
            disabled={isDeletingEvent}
            onClick={handleDeleteConfirm}
            type="button"
          >
            {isDeletingEvent ? "Deleting..." : "Delete event"}
          </button>
        </DialogActions>
      </Dialog>
      {successToast && (
        <SuccessToast key={successToast.id} title={successToast.title} />
      )}
    </div>
  );
};

export default EventsListPage;

